// ============================================================
// Moteur PUR de l'échange de plannings — AUCUNE dépendance Angular.
// Testé sans TestBed : hr-swap.spec.ts.
// ============================================================
//
// « Je me suis trompé, cette semaine c'était celle de William, pas de Valentin » — on intervertit
// les deux plannings d'un geste au lieu de tout ressaisir. Le geste, dans l'écran : on prend le nom
// d'un salarié et on le lâche sur un autre (glisser-déposer, comme les livraisons d'une tournée).
//
// L'échange porte sur les CRÉNEAUX THÉORIQUES d'une semaine du cycle (hr_slot), pas sur les
// exceptions ponctuelles : on corrige la grille, pas l'historique d'une semaine déjà passée.
// Le résultat est une nouvelle liste de créneaux, prête pour le resync en bloc du schedule.

import { HrSlot } from '@app/shared/_models/hr';

export interface SwapResult {
	slots: HrSlot[];			// la liste COMPLÈTE réécrite (contrat du resync hr_slot)
	movedA: number;				// nb de créneaux passés de A vers B
	movedB: number;
}

/// Intervertit les créneaux de deux employés sur UNE semaine du cycle d'un emploi du temps.
///
/// `weekIndex` null = toutes les semaines du cycle (cas d'une permutation durable).
/// Les créneaux des autres employés et des autres semaines sont rendus inchangés — la liste
/// retournée reste exploitable telle quelle par `buildSchedulePayload`.
export function swapEmployeeSlots(
	slots: HrSlot[],
	idA: number,
	idB: number,
	weekIndex: number | null = null,
): SwapResult {
	if (idA === idB) return { slots: slots.slice(), movedA: 0, movedB: 0 };

	let movedA = 0, movedB = 0;
	const out = slots.map(s => {
		const inScope = weekIndex === null || s.week_index === weekIndex;
		if (!inScope) return s;

		if (s.id_employee === idA) {
			movedA++;
			return Object.assign(new HrSlot(), s, { id_employee: idB });
		}
		if (s.id_employee === idB) {
			movedB++;
			return Object.assign(new HrSlot(), s, { id_employee: idA });
		}
		return s;
	});

	return { slots: out, movedA, movedB };
}

/// Ce que l'échange VIENT DE FAIRE, en une phrase. Elle s'affiche après coup, le geste (glisser un
/// nom sur un autre) étant assez explicite pour se passer d'une confirmation — et l'échange étant
/// sa propre inverse, le refaire en sens inverse annule.
export function swapSummary(result: SwapResult, nameA: string, nameB: string): string {
	if (result.movedA === 0 && result.movedB === 0) {
		return 'Aucun créneau à intervertir : ni ' + nameA + ' ni ' + nameB + ' n\'a de créneau sur cette semaine.';
	}
	const part = (n: number, name: string) =>
		n === 0 ? name + ' n\'avait aucun créneau' : n + ' créneau' + (n > 1 ? 'x' : '') + ' de ' + name;
	return part(result.movedA, nameA) + ' et ' + part(result.movedB, nameB) + ' échangés.';
}
